// src/pages/memberProfile/PartnerPersonalInfo.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { GENDERS, COUNTRIES } from "@/enums/MemberEnums";
import cls from "./PartnerPersonalInfo.module.scss";
import { toast } from "react-toastify";

const PartnerPersonalInfo = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const t = (key, vars) => i18n.t(`partnerPersonalInfo.${key}`, vars);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [realImage, setRealImage] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    country: "",
    city: "",
    address: "",
    mobilePhone: "",
    socialFacebook: "",
    socialInstagram: "",
    socialTikTok: "",
    socialYouTube: ""
  });

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await api.get(
          `/v1/member/profile/partners/${partnerId}/personal-info`
        );

        const { personal } = data || {};

        if (ignore) return;

        setRealImage(personal.realImage);

        setForm({
          firstName: personal.firstName || "",
          lastName: personal.lastName || "",
          gender: personal.gender || "",
          country: personal.country || "",
          city: personal.city || "",
          address: personal.address || "",
          mobilePhone: personal.mobilePhone || "",
          socialFacebook: personal.socialFacebook || "",
          socialInstagram: personal.socialInstagram || "",
          socialTikTok: personal.socialTikTok || "",
          socialYouTube: personal.socialYouTube || ""
        });
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [partnerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    try {
      setSaving(true);
      setError(null);

      const payload = { ...form };

      const { data } = await api.patch(
        `/v1/member/profile/partners/${partnerId}/personal-info`,
        payload
      );

      setRealImage(data.personal.realImage);

      toast.success(t("toastSaved"));
    } catch (err) {
      console.error(err);
      setError(err);
      toast.error(t("toastError"));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/member/my-profile/partners/${partnerId}`);
  };

  return (
    <section className={cls.page}>
      {/* Header */}
      <header className={cls.header}>
        <h1 className={cls.title}>{t("title")}</h1>
        <p className={cls.subtitle}>{t("subtitle")}</p>

        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon}>←</span>
          {t("backToPartner")}
        </button>
      </header>

      {/* Loading */}
      {loading && (
        <div className={cls.stateBox}>
          <span className={cls.spinner} />
          <span>{t("loading")}</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className={cls.stateBoxError}>
          <p>{t("errorLoad")}</p>
        </div>
      )}

      {/* Form */}
      {!loading && !error && (
        <form className={cls.form} onSubmit={handleSubmit}>
          <div className={cls.grid}>
            {/* SIDE COLUMN */}
            <aside className={cls.colSide}>
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>{t("sections.verification.title")}</h2>
                <p className={cls.cardHint}>{t("sections.verification.hint")}</p>

                {realImage?.url ? (
                  <div className={cls.realImageBox}>
                    <div className={cls.realImageFrame}>
                      <img
                        src={realImage.url}
                        alt={t("sections.verification.alt")}
                        className={cls.realImage}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={cls.realImageEmpty}>
                    <p>{t("sections.verification.empty")}</p>
                  </div>
                )}

                <p className={cls.realImageNote}>{t("sections.verification.note")}</p>
              </section>
            </aside>

            {/* MAIN COLUMN */}
            <div className={cls.colMain}>
              {/* Legal name & gender */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>{t("sections.legalName.title")}</h2>
                <p className={cls.cardHint}>{t("sections.legalName.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRow}>
                    <label className={cls.label}>{t("fields.firstName")}</label>
                    <input
                      name="firstName"
                      className={cls.input}
                      value={form.firstName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRow}>
                    <label className={cls.label}>{t("fields.lastName")}</label>
                    <input
                      name="lastName"
                      className={cls.input}
                      value={form.lastName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.gender")}</label>
                    <select
                      name="gender"
                      className={cls.input}
                      value={form.gender}
                      onChange={handleChange}
                    >
                      <option value="">{t("placeholders.select")}</option>
                      {GENDERS.map((g) => (
                        <option key={g} value={g}>
                          {t(`genders.${g}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* Location */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>{t("sections.location.title")}</h2>
                <p className={cls.cardHint}>{t("sections.location.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRow}>
                    <label className={cls.label}>{t("fields.country")}</label>
                    <select
                      name="country"
                      className={cls.input}
                      value={form.country}
                      onChange={handleChange}
                    >
                      <option value="">{t("placeholders.select")}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={cls.fieldRow}>
                    <label className={cls.label}>{t("fields.city")}</label>
                    <input
                      name="city"
                      className={cls.input}
                      value={form.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.address")}</label>
                    <input
                      name="address"
                      className={cls.input}
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.mobilePhone")}</label>
                    <input
                      name="mobilePhone"
                      className={cls.input}
                      value={form.mobilePhone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>

              {/* Socials */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>{t("sections.socials.title")}</h2>
                <p className={cls.cardHint}>{t("sections.socials.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.socialFacebook")}</label>
                    <input
                      name="socialFacebook"
                      className={cls.input}
                      value={form.socialFacebook}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.socialInstagram")}</label>
                    <input
                      name="socialInstagram"
                      className={cls.input}
                      value={form.socialInstagram}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.socialTikTok")}</label>
                    <input
                      name="socialTikTok"
                      className={cls.input}
                      value={form.socialTikTok}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label}>{t("fields.socialYouTube")}</label>
                    <input
                      name="socialYouTube"
                      className={cls.input}
                      value={form.socialYouTube}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className={cls.actionsRow}>
            <button type="submit" className={cls.saveBtn} disabled={saving}>
              {saving ? t("saving") : t("saveChanges")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default PartnerPersonalInfo;
